import { gql } from "@apollo/client";
import Stack, { PageContentStack } from "atoms/Stack";
import { MAX_CONTENT_WIDTH } from "helpers/breakpoints";
import { gaSetSharedEventParams } from "helpers/gtag";
import { SPACING } from "helpers/spacings";
import useData from "helpers/useData";
import useEnforceAuth from "helpers/useEnforceAuth";
import PersonCard, { PersonCardFragment, PersonCardStat } from "molecules/PersonCard";
import Section from "molecules/Section";
import React from "react";
import NotFoundPage from "shared/NotFoundPage";
import { TeamPageTitle } from "shared/PageTitle";

import TeamPagesHeader from "./team/TeamPagesHeader";

export default function TeamRosterPage({ teamId }) {
  useEnforceAuth({ enforceAuthenticated: true });
  const [permissionData] = useData(
    gql`
      query TeamPagesHeader_permission($teamId: ID!) {
        team(id: $teamId) {
          id
          org {
            id
            gaId
          }
          canOnsenTeamPage
        }
      }
    `,
    { teamId },
  );
  gaSetSharedEventParams({ org_id: permissionData?.team?.org?.gaId });

  const [data] = useData(
    gql`
      query TeamRosterPage($teamId: ID!) {
        team(id: $teamId) {
          id
          players: teamMembers(role: "player") {
            id
            playerNumber
            person {
              id
              ...PersonCardFragment
            }
            gamesCount
            pointsPerGame
            reboundsPerGame
            assistsPerGame
            blocksPerGame
            stealsPerGame
          }
          coaches: teamMembers(role: "coach") {
            id
            person {
              id
              ...PersonCardFragment
            }
          }
          org {
            id
            primarySport
          }
        }
      }
      ${PersonCardFragment}
    `,
    { teamId },
  );

  if (permissionData && !permissionData.team?.canOnsenTeamPage) return <NotFoundPage />;

  return (
    <>
      <TeamPageTitle title="Roster" />
      <TeamPagesHeader teamId={teamId} tab="roster" />
      <PageContentStack>
        <Stack style={{ margin: "0 auto", maxWidth: MAX_CONTENT_WIDTH, width: `calc(100vw - ${SPACING} * 2)` }}>
          <Section title="Players">
            <Stack horizontal>
              {data?.team?.players.length === 0 && <div>No players</div>}
              {data?.team?.players.map((player) => (
                <PersonCard
                  playerNumber={player.playerNumber || "?"}
                  key={player.id}
                  person={player.person}
                  stats={
                    <>
                      <PersonCardStat label="Games" value={player.gamesCount} />
                      {!["pickleball", "netball", "volleyball"].includes(data?.team?.org?.primarySport) && (
                        <>
                          <PersonCardStat label="PPG" color={"rgba(37,37,37,0.9)"} value={player.pointsPerGame} />
                          <PersonCardStat label="RPG" color={"rgba(37,37,37,0.9)"} value={player.reboundsPerGame} />
                          <PersonCardStat label="APG" color={"rgba(37,37,37,0.9)"} value={player.assistsPerGame} />
                          <PersonCardStat label="BPG" color={"rgba(37,37,37,0.9)"} value={player.blocksPerGame} />
                          <PersonCardStat label="SPG" color={"rgba(37,37,37,0.9)"} value={player.stealsPerGame} />
                        </>
                      )}
                    </>
                  }
                />
              ))}
            </Stack>
          </Section>
          <Section title="Coaches">
            <Stack horizontal>
              {data?.team?.coaches.length === 0 && <div>No coaches</div>}
              {data?.team?.coaches.map((coach) => (
                <PersonCard key={coach.id} person={coach.person} />
              ))}
            </Stack>
          </Section>
        </Stack>
      </PageContentStack>
    </>
  );
}
